import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, IconButton, List, ListItem } from '@ds';
import {
  DSArrowDownIcon,
  DSArrowRightIcon,
  DSDeleteIcon,
  DSInsertAboveIcon,
  DSInsertBelowIcon,
  DSMoreOptionsVerticalIcon
} from '@ds-icons';
import Collapse from '@material-ui/core/Collapse';
import Popover from '@material-ui/core/Popover';
import cx from 'classnames';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import alertsUtil from '@/util/alertsUtil';
import { BasicModal } from '@DesignSystem/modals';
import DashboardSectionDragIcon from './DashboardSectionDragIcon.svg';
import { DashboardSectionTitle } from './DashboardSectionTitle';
import { DashboardSectionEmptyState } from './DashboardSectionEmptyState';

import styles from './DashboardSection.module.scss';

export type LayoutItem = {
  i: string;
  x: number;
  y: number;
  h: number;
  w: number;
};

export type IDashboardSection<SectionItem> = {
  id: string;
  isExpanded: boolean;
  items: SectionItem[];
  layout: LayoutItem[];
  title: string;
};

type DashboardSectionProps<SectionItem> = {
  isDragPreview?: boolean;
  onAddItemClick: (id: string) => void;
  onAddSectionById: (id: string, shift: number) => void;
  onDeleteSection: (id: string) => void;
  onSectionChange: (
    id: string,
    value: Partial<IDashboardSection<SectionItem>>
  ) => void;
  renderItems: (
    id: string,
    items: SectionItem[],
    layout: LayoutItem[]
  ) => React.ReactNode;
  scrollToRef?: (node: HTMLElement) => void;
  section: IDashboardSection<SectionItem>;
};

export function DashboardSection<SectionItem>({
  isDragPreview = false,
  onAddItemClick,
  onAddSectionById,
  onDeleteSection,
  onSectionChange,
  renderItems,
  scrollToRef,
  section
}: DashboardSectionProps<SectionItem>) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const { items } = section;
  const hasItems = items.length > 0;
  const menuOpened = Boolean(anchorEl);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isSorting,
    isDragging
  } = useSortable({
    id: section.id
  });

  const handleToggleSection = useCallback(() => {
    onSectionChange(section.id, {
      isExpanded: !section.isExpanded
    });
  }, [onSectionChange, section.id, section.isExpanded]);

  const handleAddItemClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!section.isExpanded) {
        handleToggleSection();
      }
      onAddItemClick(section.id);
    },
    [handleToggleSection, onAddItemClick, section.id, section.isExpanded]
  );

  const handleAddAboveClick = useCallback(() => {
    setAnchorEl(null);
    onAddSectionById(section.id, 0);
  }, [onAddSectionById, section.id]);

  const handleAddBelowClick = useCallback(() => {
    setAnchorEl(null);
    onAddSectionById(section.id, 1);
  }, [onAddSectionById, section.id]);

  const handleDeleteClick = useCallback(() => {
    setAnchorEl(null);
    const POPUP_ID = 'CONFIRM_SECTION_DELETE';

    const closeHandler = () => {
      dispatch(alertsUtil.closeDialog(POPUP_ID));
    };

    const confirmHandler = () => {
      closeHandler();
      onDeleteSection(section.id);
    };

    dispatch(
      alertsUtil.openCustomModal(
        POPUP_ID,
        <BasicModal
          open
          className={styles.confirmPopup}
          title={`Delete ${section.title} section?`}
          primaryButtonText="Yes, delete"
          secondaryButtonText="Cancel"
          onClose={closeHandler}
          onPrimaryButtonClick={confirmHandler}
          onSecondaryButtonClick={closeHandler}
          content={
            <div className={styles.confirmContent}>
              {`The '${section.title}' section will be permanently deleted and cannot be recovered.`}
            </div>
          }
        />
      )
    );
  }, [dispatch, onDeleteSection, section.id, section.title]);

  const handleTitleChange = useCallback(
    (title: string) => onSectionChange(section.id, { title }),
    [onSectionChange, section.id]
  );

  const renderItemContainer = () => {
    if (!hasItems) {
      return (
        <DashboardSectionEmptyState
          onAddItemButtonClick={() => onAddItemClick(section.id)}
        />
      );
    }

    return renderItems(section.id, items, section.layout);
  };

  const renderAddItemButton = () => {
    return (
      <Button
        type="primary"
        size="medium"
        onClick={handleAddItemClick}
        data-test="add-panel-button"
      >
        Add panel
      </Button>
    );
  };

  const renderMenuButton = () => {
    return (
      <div onClick={event => event.stopPropagation()}>
        <IconButton
          type="secondary"
          Icon={<DSMoreOptionsVerticalIcon />}
          active={menuOpened}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(event.currentTarget)
          }
          data-test="section-menu-button"
        />
        <Popover
          open={menuOpened}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          classes={{ paper: styles.popoverContainer }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: -6,
            horizontal: 'right'
          }}
        >
          <div className={styles.listContainer}>
            <List>
              <ListItem
                onClick={handleAddAboveClick}
                data-test="add-above-panel-button"
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSInsertAboveIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Insert section above" />
              </ListItem>

              <ListItem
                onClick={handleAddBelowClick}
                data-test="add-below-panel-button"
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSInsertBelowIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Insert section below" />
              </ListItem>

              <ListItem
                onClick={handleDeleteClick}
                data-test="delete-panel-button"
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSDeleteIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Delete section" />
              </ListItem>
            </List>
          </div>
        </Popover>
      </div>
    );
  };

  const style = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cx(styles.itemSectionWrapper, {
        [styles.expanded]: !isSorting && section.isExpanded,
        [styles.empty]: !hasItems,
        [styles.menuOpened]: menuOpened,
        [styles.sorting]: isSorting,
        [styles.preview]: isDragPreview
      })}
      data-test="dashboard-section"
    >
      <div
        ref={scrollToRef as never}
        className={styles.header}
        onClick={handleToggleSection}
        data-test="dashboard-section-header"
      >
        <div className={styles.left}>
          {section.isExpanded ? <DSArrowDownIcon /> : <DSArrowRightIcon />}
          <DashboardSectionTitle
            placeholder="New section"
            title={section.title}
            count={items.length}
            onChange={handleTitleChange}
          />
        </div>
        <div className={styles.right}>
          <DashboardSectionDragIcon className={styles.drag} {...listeners} />
          {renderMenuButton()}
          {renderAddItemButton()}
        </div>
      </div>
      {!isDragPreview && (
        <div className={styles.body}>
          <Collapse in={section.isExpanded} timeout={0}>
            {section.isExpanded ? renderItemContainer() : null}
          </Collapse>
        </div>
      )}
    </div>
  );
}
